import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import { authClient } from "../../lib/auth/client";

type AuthStatus = "authenticated" | "loading" | "anonymous";

type AuthContextValue = {
  refreshSession: () => Promise<void>;
  session: typeof authClient.$Infer.Session | null;
  signOut: () => Promise<void>;
  status: AuthStatus;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** 通过 Better Auth 恢复并订阅 HttpOnly Cookie 会话。 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending, refetch } = authClient.useSession();
  const status: AuthStatus = isPending
    ? "loading"
    : session
      ? "authenticated"
      : "anonymous";

  const value = useMemo<AuthContextValue>(
    () => ({
      refreshSession: async () => {
        await refetch();
      },
      session: session ?? null,
      status,
      signOut: async () => {
        const { error } = await authClient.signOut();
        if (error) throw error;
      },
    }),
    [refetch, session, status],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** 读取已由根 Provider 建立的 Better Auth 会话上下文。 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
